import React, { useRef, useState } from 'react';
import { Alert, Box, Card, CardContent, Link, Stack, TextField, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Link as RouterLink } from 'react-router-dom';
import { useAPIUnauthenticatedService } from '../helpers/APICallsUnauthenticated';

const DeleteAccount: React.FC = () => {
  const api = useAPIUnauthenticatedService();
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (inFlight.current || sent) return;

    const registeredEmail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registeredEmail)) {
      setError('Enter a valid registered email address.');
      return;
    }

    inFlight.current = true;
    setPending(true);
    setError('');
    try {
      // REQ-056: email a request for manual verification; submission never deletes an account.
      await api.sendMail(
        `ACCOUNT DELETION REQUEST - In House Property Hub\n\nRegistered email: ${registeredEmail}\n\nPlease delete my account and associated personal data, subject to necessary financial and legal retention.\n\nBack-office action: verify account ownership before processing and confirm completion and any retained records to the account holder.`,
        registeredEmail,
        'Account deletion request'
      );
      setSent(true);
      setEmail('');
    } catch {
      // REQ-056: do not expose backend errors or suggest that a failed request was delivered.
      setError('We could not send your request. Please try again or email team@inhouseapp.co.uk.');
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  return (
    <Card sx={{ width: '100%', maxWidth: 680 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={3}>
          <Box>
            <Typography component="h1" variant="h4" gutterBottom>Request account deletion</Typography>
            <Typography>In House Property Hub, operated by Alenzo Ltd (IN HOUSE)</Typography>
          </Box>
          <Box>
            <Typography component="h2" variant="h6">How to request deletion</Typography>
            <Box component="ol" sx={{ pl: 3 }}>
              <li>Enter the email address registered to your account and submit the request below.</li>
              <li>Our team will contact you to verify account ownership before deleting anything.</li>
              <li>We will process the request manually and confirm completion, explaining any records we need to retain.</li>
            </Box>
            <Typography>You can request deletion here without logging in or reinstalling the app.</Typography>
          </Box>
          {sent ? (
            <Alert severity="success" role="status">
              Your deletion request has been sent. Your account has not yet been deleted. Our team will contact you to verify ownership and process your request.
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2}>
                <TextField
                  label="Registered email address"
                  type="email"
                  autoComplete="email"
                  required
                  fullWidth
                  value={email}
                  disabled={pending}
                  onChange={(event) => { setEmail(event.target.value); setError(''); }}
                  inputProps={{ maxLength: 254 }}
                />
                {error && <Alert severity="error">{error}</Alert>}
                <LoadingButton type="submit" variant="contained" loading={pending} disabled={pending}>
                  {pending ? 'Sending request...' : 'Request account deletion'}
                </LoadingButton>
              </Stack>
            </Box>
          )}
          <Box>
            <Typography component="h2" variant="h6" gutterBottom>What is deleted or retained</Typography>
            <Typography paragraph>
              We delete your login account, profile and associated personal data that we no longer need, including unnecessary referral details, messages and notification registrations.
            </Typography>
            <Typography paragraph>
              We retain necessary referral fee, invoice and payment records for six years from the end of the last company financial year they relate to. A longer period may apply where required by law or an ongoing tax investigation.
            </Typography>
            <Typography>
              For outstanding referrals, unpaid fees or disputes, we may retain the minimum information needed to settle obligations or establish, exercise or defend legal claims. We will explain any applicable retention period when responding. Account deletion does not cancel fees owed or money due to you.
            </Typography>
          </Box>
          <Typography>
            Alternatively, email <Link href="mailto:team@inhouseapp.co.uk?subject=Account%20deletion%20request">team@inhouseapp.co.uk</Link> from your registered email address with the subject &quot;Account deletion request&quot;. Do not send your password.
          </Typography>
          {/* REQ-057: link retention details directly from the deletion request flow. */}
          <Link component={RouterLink} to="/data-retention-and-deletion">Data retention and account deletion policy</Link>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DeleteAccount;
